import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useEffect, useRef } from 'react';
import type { BusPosition } from '../lib/types';

// SVG bus icon
const busSvg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">
  <circle cx="20" cy="20" r="18" fill="#e94560" stroke="#fff" stroke-width="2"/>
  <text x="20" y="26" text-anchor="middle" fill="#fff" font-size="18" font-family="Arial">🚌</text>
</svg>`;

const busIcon = L.divIcon({
  html: busSvg,
  className: 'bus-marker',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

interface BusMarkerProps {
  position: BusPosition;
  busNumber: string;
  driverName?: string | null;
  nearestStop?: string | null;
}

export function BusMarkerComponent({
  position,
  busNumber,
  driverName,
  nearestStop,
}: BusMarkerProps) {
  const markerRef = useRef<L.Marker>(null);

  // Smooth animation: slide marker to new position
  useEffect(() => {
    if (markerRef.current) {
      const marker = markerRef.current;
      const newLatLng = L.latLng(position.lat, position.lng);
      const currentLatLng = marker.getLatLng();

      // Animate over 1 second
      const steps = 20;
      const latStep = (newLatLng.lat - currentLatLng.lat) / steps;
      const lngStep = (newLatLng.lng - currentLatLng.lng) / steps;
      let step = 0;

      const animate = () => {
        step++;
        if (step <= steps) {
          marker.setLatLng([
            currentLatLng.lat + latStep * step,
            currentLatLng.lng + lngStep * step,
          ]);
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [position.lat, position.lng]);

  const speedKmh = position.speed ? (position.speed * 3.6).toFixed(0) : '0';
  const lastUpdate = new Date(position.ts).toLocaleTimeString();

  return (
    <Marker
      ref={markerRef}
      position={[position.lat, position.lng]}
      icon={busIcon}
    >
      <Popup>
        <div style={{ fontSize: '14px', lineHeight: 1.5 }}>
          <strong>{busNumber}</strong>
          {nearestStop && <div style={{ color: '#4ade80', fontWeight: 600 }}>Near: {nearestStop}</div>}
          {driverName && <div>Driver: {driverName}</div>}
          <div>Speed: {speedKmh} km/h</div>
          <div>Updated: {lastUpdate}</div>
          {position.accuracy && (
            <div>Accuracy: {Math.round(position.accuracy)}m</div>
          )}
        </div>
      </Popup>
    </Marker>
  );
}
